import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Lock, RefreshCw, Check, X, Truck, ChevronDown } from 'lucide-react';
import { schedulerApiFetch } from '../api/schedulerClient';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const IN_MOTION_STATUSES = new Set(['onway', 'arrived', 'started', 'inprogress', 'in_progress']);
const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'canceled', 'no_show', 'noshow']);

function normalizeId(v) {
  if (!v) return '';
  if (typeof v === 'object') return normalizeId(v._id || v.id || v.$oid);
  return String(v).trim();
}

/** Compact multi-select dropdown for filter bar (checkbox list). */
function MultiSelectFilter({ labelAll, options, selected, onChange, widthClass = 'w-44' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (opt) => {
    const next = new Set(selectedSet);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };

  const count = selectedSet.size;
  const buttonLabel = count === 0
    ? labelAll
    : count === 1
      ? Array.from(selectedSet)[0]
      : `${count} selected`;

  return (
    <div className={`relative ${widthClass} shrink-0`} ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full min-w-0 items-center justify-between gap-1 rounded-md border border-main-base bg-white px-2 py-1.5 text-left text-xs text-main-primary shadow-sm focus:border-main-primary focus:outline-none focus:ring-1 focus:ring-main-primary"
        title={count ? Array.from(selectedSet).join(', ') : labelAll}
      >
        <span className="min-w-0 truncate font-medium">{buttonLabel}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-main-light transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-[60] mt-1 max-h-56 w-56 overflow-auto rounded-md border border-main-base bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[11px] font-bold uppercase tracking-wider text-main-light hover:bg-main-primary/5 hover:text-main-primary"
          >
            {labelAll}
          </button>
          {options.length === 0 ? (
            <div className="px-2.5 py-2 text-[11px] text-gray-400">No options</div>
          ) : (
            options.map((opt) => {
              const isOn = selectedSet.has(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => toggle(opt)}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                >
                  <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${isOn ? 'border-slate-900 bg-slate-900' : 'border-slate-300 bg-white'}`}>
                    {isOn && <Check className="h-2.5 w-2.5 text-white stroke-[3px]" />}
                  </span>
                  <span className="truncate" title={opt}>{opt}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function fmtTime(iso, tz) {
  if (!iso) return '';
  try {
    return formatInTimeZone(new Date(iso), tz, 'MMM d, h:mm a');
  } catch {
    return '';
  }
}

/** Resolve space-types entry: prefer Space Type id, then LOS id (same as planner mapping). */
function getSpaceTypesEntry(loadUnloadTimes, spaceTypeId, losId) {
  const st = normalizeId(spaceTypeId).toLowerCase();
  const los = normalizeId(losId).toLowerCase();
  return (st && loadUnloadTimes?.[st]) || (los && loadUnloadTimes?.[los]) || null;
}

/** Display: FL: LOS (SpaceType) — e.g. FL: AMB (AMBC2C) */
function getFleetLevelDisplay(loadUnloadTimes, spaceTypeId, losId) {
  const entry = getSpaceTypesEntry(loadUnloadTimes, spaceTypeId, losId);
  if (!entry) return '';
  const losPart = String(entry.los_title || entry.category || '').trim() || '—';
  const stPart = String(entry.st_title || '').trim() || '—';
  return `FL: ${losPart} ( ${stPart} )`;
}

/** Resolve service area display name from id → name map. */
function getServiceAreaName(serviceAreaMap, serviceAreaId) {
  const id = normalizeId(serviceAreaId);
  if (!id) return '';
  return String(serviceAreaMap?.[id.toLowerCase()] || '').trim();
}

function getFundingSourceName(cooperateNames, cooperateId) {
  const id = normalizeId(cooperateId);
  if (!id) return '';
  return String(cooperateNames?.[id] || cooperateNames?.[id.toLowerCase()] || '').trim();
}

const TRIPS_COLUMNS = [
  { label: '#', width: '45px' },
  { label: 'SELECT', width: '55px' },
  { label: 'TRIP ID', width: '110px' },
  { label: 'CLIENT', width: '180px' },
  { label: 'PU TIME', width: '110px' },
  { label: 'DO TIME', width: '110px' },
  { label: 'PICKUP ADDRESS', width: '240px' },
  { label: 'DROPOFF ADDRESS', width: '240px' },
  { label: 'MI', width: '55px' },
  { label: 'STATUS', width: '100px' },
  { label: 'FLEET / SPACE', width: '160px' },
  { label: 'SERVICE AREA', width: '140px' },
  { label: 'FUNDING', width: '140px' },
  { label: 'DRIVER', width: '140px' },
];

export default function TripsSelectionTab({
  schedulerApiUrl,
  customPlanningDate,
  customPlanningEndDate,
  getAuthToken,
  companyTimezone,
  frozenDriverIds = [],
  freezePreAssignDriverIds = [],
  preservePortalBuckets = false,
  selectedDriverIdsOrNull = null,
  syncTrigger = 0,
  onSelectedTripIdsChange,
  /** Controlled selection (array). When provided, parent owns selection. */
  selectedTripIds: selectedTripIdsProp,
  /** When onRefreshExternal is provided, use prefetched/external trip data (no self-fetch). */
  onRefreshExternal,
  externalTrips,
  externalStatusCounts,
  externalExcludedBreakTrips,
  externalLoadUnloadTimes,
  externalServiceAreaMap,
  externalCooperateNames,
  externalLoading = false,
  externalError = null,
}) {
  const tz = companyTimezone || 'America/Los_Angeles';
  const useExternalPreview = typeof onRefreshExternal === 'function';
  const selectionControlled = Array.isArray(selectedTripIdsProp);

  const [internalLoading, setIsLoading] = useState(false);
  const [internalError, setError] = useState(null);
  const [internalTrips, setTrips] = useState([]);
  const [internalStatusCounts, setStatusCounts] = useState({});
  const [internalExcludedBreakTrips, setExcludedBreakTrips] = useState(0);
  const [internalLoadUnloadTimes, setLoadUnloadTimes] = useState({});
  const [internalServiceAreaMap, setServiceAreaMap] = useState({});
  const [internalCooperateNames, setCooperateNames] = useState({});

  const isLoading = useExternalPreview ? !!externalLoading : internalLoading;
  const error = useExternalPreview ? externalError : internalError;
  const trips = useExternalPreview ? (externalTrips || []) : internalTrips;
  const statusCounts = useExternalPreview ? (externalStatusCounts || {}) : internalStatusCounts;
  const excludedBreakTrips = useExternalPreview
    ? (externalExcludedBreakTrips || 0)
    : internalExcludedBreakTrips;
  const loadUnloadTimes = useExternalPreview
    ? (externalLoadUnloadTimes || {})
    : internalLoadUnloadTimes;
  const serviceAreaMap = useExternalPreview
    ? (externalServiceAreaMap || {})
    : internalServiceAreaMap;
  const cooperateNames = useExternalPreview
    ? (externalCooperateNames || {})
    : internalCooperateNames;

  // Filters (search by member name + trip display id only)
  const [searchQuery, setSearchQuery] = useState('');
  const [spaceTypeFilter, setSpaceTypeFilter] = useState([]); // selected st_title strings (multi)
  const [serviceAreaFilter, setServiceAreaFilter] = useState([]); // selected service area names (multi)
  const [fundingFilter, setFundingFilter] = useState([]); // selected funding displayName strings (multi)
  const [statusFilter, setStatusFilter] = useState(''); // job_status string (lowercase)
  const [timeStartLocal, setTimeStartLocal] = useState(null); // Dayjs time-only
  const [timeEndLocal, setTimeEndLocal] = useState(null); // Dayjs time-only

  const [internalSelectedTripIds, setInternalSelectedTripIds] = useState(() => new Set());
  const selectedTripIds = selectionControlled
    ? new Set(selectedTripIdsProp)
    : internalSelectedTripIds;

  const setSelectedTripIds = (updater) => {
    if (selectionControlled) {
      const prev = new Set(selectedTripIdsProp);
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onSelectedTripIdsChange?.(Array.from(next));
      return;
    }
    setInternalSelectedTripIds(updater);
  };

  const frozenSet = useMemo(() => new Set((frozenDriverIds || []).map(normalizeId)), [frozenDriverIds]);
  const freezePreSet = useMemo(() => new Set((freezePreAssignDriverIds || []).map(normalizeId)), [freezePreAssignDriverIds]);
  const selectedDriverSet = useMemo(() => {
    if (!Array.isArray(selectedDriverIdsOrNull)) return null;
    return new Set(selectedDriverIdsOrNull.map(normalizeId).filter(Boolean));
  }, [selectedDriverIdsOrNull]);

  const enriched = useMemo(() => {
    return (trips || [])
      .map(t => {
        const tripId = normalizeId(t.trip_object_id || t.trip_id);
        const displayId = String(t.trip_display_id || '').trim();
        const driverId = normalizeId(t.driver_id);
        const st = String(t.job_status || '').toLowerCase();

        let lockReason = '';
        if (driverId && frozenSet.has(driverId)) lockReason = 'Freeze';
        else if (driverId && freezePreSet.has(driverId)) lockReason = 'Freeze Pre-Assign';
        else if (
          preservePortalBuckets
          && selectedDriverSet
          && driverId
          && !selectedDriverSet.has(driverId)
        ) lockReason = 'Preserved';
        else if (IN_MOTION_STATUSES.has(st)) lockReason = 'In progress';

        const isLocked = !!lockReason;
        const isTerminal = TERMINAL_STATUSES.has(st);

        return {
          ...t,
          _tripId: tripId,
          _displayTripId: displayId,
          _driverId: driverId,
          _status: st,
          _fundingName: getFundingSourceName(cooperateNames, t.cooperate_id),
          _isLocked: isLocked,
          _lockReason: lockReason,
          _isTerminal: isTerminal,
        };
      })
      .filter(t => !!t._tripId); // keep only valid ids
  }, [trips, frozenSet, freezePreSet, preservePortalBuckets, selectedDriverSet, cooperateNames]);

  const lockedTripIds = useMemo(() => new Set(enriched.filter(t => t._isLocked).map(t => t._tripId)), [enriched]);

  const spaceTypeOptions = useMemo(() => {
    const s = new Set();
    enriched.forEach(t => {
      const entry = getSpaceTypesEntry(loadUnloadTimes, t.space_type_id, t.los_id);
      const stTitle = String(entry?.st_title || '').trim();
      if (stTitle) s.add(stTitle);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [enriched, loadUnloadTimes]);

  const serviceAreaOptions = useMemo(() => {
    const s = new Set();
    enriched.forEach(t => {
      const name = getServiceAreaName(serviceAreaMap, t.service_area_id);
      if (name) s.add(name);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [enriched, serviceAreaMap]);

  const fundingOptions = useMemo(() => {
    const s = new Set();
    enriched.forEach(t => {
      if (t._fundingName) s.add(t._fundingName);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [enriched]);

  const timeWindow = useMemo(() => {
    // Interpret time-only pickers in company timezone on the planning day.
    const base = formatInTimeZone(customPlanningDate || new Date(), tz, 'yyyy-MM-dd');
    const start = timeStartLocal && dayjs(timeStartLocal).isValid()
      ? fromZonedTime(`${base}T${dayjs(timeStartLocal).format('HH:mm')}`, tz)
      : null;
    const end = timeEndLocal && dayjs(timeEndLocal).isValid()
      ? fromZonedTime(`${base}T${dayjs(timeEndLocal).format('HH:mm')}`, tz)
      : null;
    return { start, end };
  }, [timeStartLocal, timeEndLocal, tz, customPlanningDate]);

  /** Search + space type + service area + time only (excludes status chip filter) — used for list + status counts. */
  const nonStatusFilteredTrips = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    const stfSet = new Set((spaceTypeFilter || []).map(v => String(v).trim().toLowerCase()).filter(Boolean));
    const safSet = new Set((serviceAreaFilter || []).map(v => String(v).trim().toLowerCase()).filter(Boolean));
    const fundingSet = new Set(fundingFilter || []);
    const { start, end } = timeWindow;

    return enriched.filter(t => {
      // Search: member name + displayed trip id only
      if (q) {
        const member = String(t.member_name || '').toLowerCase();
        const tripDisplay = String(t._displayTripId || '').toLowerCase();
        if (!member.includes(q) && !tripDisplay.includes(q)) return false;
      }

      // Space Type filter (by st_title; multi-select OR)
      if (stfSet.size > 0) {
        const entry = getSpaceTypesEntry(loadUnloadTimes, t.space_type_id, t.los_id);
        const stTitle = String(entry?.st_title || '').trim().toLowerCase();
        if (!stfSet.has(stTitle)) return false;
      }

      // Service Area filter (by resolved name; multi-select OR)
      if (safSet.size > 0) {
        const areaName = getServiceAreaName(serviceAreaMap, t.service_area_id).toLowerCase();
        if (!safSet.has(areaName)) return false;
      }

      // Funding source filter (by displayName; multi-select OR)
      if (fundingSet.size > 0) {
        if (!t._fundingName || !fundingSet.has(t._fundingName)) return false;
      }

      // Time window filter (company timezone), include if PU OR DO inside window (inclusive)
      if (start || end) {
        const puIso = t.pickup_time_resolved || t.pickup_time || t.schedule_time;
        const doIso = t.dropoff_time_resolved || t.dropoff_time || t.appointment_time;
        const pu = puIso ? new Date(puIso) : null;
        const d0 = doIso ? new Date(doIso) : null;

        const inRange = (dt) => {
          if (!dt || Number.isNaN(dt.getTime())) return false;
          if (start && dt < start) return false;
          if (end && dt > end) return false;
          return true;
        };

        if (!inRange(pu) && !inRange(d0)) return false;
      }

      return true;
    });
  }, [enriched, loadUnloadTimes, serviceAreaMap, spaceTypeFilter, serviceAreaFilter, fundingFilter, searchQuery, timeWindow]);

  const filteredTrips = useMemo(() => {
    const sf = (statusFilter || '').trim().toLowerCase();
    if (!sf) return nonStatusFilteredTrips;
    return nonStatusFilteredTrips.filter(t => String(t._status || '').toLowerCase() === sf);
  }, [nonStatusFilteredTrips, statusFilter]);

  /** Per-status counts for trips matching current search / space / time (not status chip). */
  const filteredStatusCounts = useMemo(() => {
    const counts = {};
    nonStatusFilteredTrips.forEach(t => {
      const k = String(t._status || '').toLowerCase() || '(none)';
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [nonStatusFilteredTrips]);

  /** Preserve API chip order; values reflect filtered subset. Include extra statuses if any. */
  const statusChipEntries = useMemo(() => {
    const fc = filteredStatusCounts;
    const apiKeys = Object.keys(statusCounts || {});
    const seen = new Set();
    const entries = [];
    apiKeys.forEach((k) => {
      const norm = String(k || '').toLowerCase() || '(none)';
      if (seen.has(norm)) return;
      seen.add(norm);
      entries.push({ key: norm, count: fc[norm] ?? 0 });
    });
    Object.keys(fc)
      .filter(k => !seen.has(k))
      .sort((a, b) => a.localeCompare(b))
      .forEach(k => entries.push({ key: k, count: fc[k] }));
    return entries;
  }, [statusCounts, filteredStatusCounts]);

  // Default selection: select all ON FIRST LOAD of a new result set.
  // Do not keep re-applying default selection or it will fight user unselects.
  // Skipped when parent controls selection (drawer prefetch path).
  useEffect(() => {
    if (selectionControlled) return;
    if (!enriched.length) return;
    setSelectedTripIds(prev => {
      if (prev && prev.size > 0) return prev;
      return new Set(enriched.map(t => t._tripId));
    });
  }, [enriched, selectionControlled]);

  // Notify parent with effective selected ids (locked are included since selectedTripIds includes all by default)
  useEffect(() => {
    if (selectionControlled) return;
    if (!onSelectedTripIdsChange) return;
    onSelectedTripIdsChange(Array.from(selectedTripIds));
  }, [selectedTripIds, onSelectedTripIdsChange, selectionControlled]);

  const fetchTrips = async () => {
    if (useExternalPreview) {
      // Manual refresh only — never restart an in-flight prefetch just by opening this tab
      await onRefreshExternal?.({ force: true });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (customPlanningDate) params.append('custom_datetime_iso', customPlanningDate.toISOString());
      if (customPlanningEndDate) params.append('custom_datetime_end_iso', customPlanningEndDate.toISOString());
      params.append('_t', Date.now());

      const authToken = getAuthToken ? await getAuthToken() : null;

      const [tripsRes, spaceRes, serviceAreaRes] = await Promise.all([
        schedulerApiFetch(`preview-trips?${params.toString()}`, {
          bearerToken: authToken || undefined,
        }),
        schedulerApiFetch('space-types', {
          cacheBust: true,
          bearerToken: authToken || undefined,
        }),
        schedulerApiFetch('service-areas', {
          cacheBust: true,
          bearerToken: authToken || undefined,
        }),
      ]);

      if (!tripsRes.ok) throw new Error(`Failed to fetch trips: ${tripsRes.status}`);
      const tripsJson = await tripsRes.json();
      if (!tripsJson.success) throw new Error(tripsJson.message || 'Failed to fetch trips');
      setTrips(tripsJson.trips || []);
      setStatusCounts(tripsJson.status_counts || {});
      setExcludedBreakTrips(tripsJson.excluded_break_trips || 0);
      setCooperateNames(tripsJson.cooperate_names || {});

      if (spaceRes.ok) {
        const spaceJson = await spaceRes.json();
        if (spaceJson?.success && spaceJson?.load_unload_times) {
          // Normalize keys to lowercase for lookups
          const next = {};
          Object.entries(spaceJson.load_unload_times).forEach(([k, v]) => {
            next[String(k).toLowerCase()] = v;
          });
          setLoadUnloadTimes(next);
        }
      }

      if (serviceAreaRes.ok) {
        const serviceAreaJson = await serviceAreaRes.json();
        if (serviceAreaJson?.success && serviceAreaJson?.service_areas) {
          const next = {};
          Object.entries(serviceAreaJson.service_areas).forEach(([k, v]) => {
            next[String(k).toLowerCase()] = v;
          });
          setServiceAreaMap(next);
        }
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (useExternalPreview) return;
    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPlanningDate, customPlanningEndDate, syncTrigger, useExternalPreview]);

  const stats = useMemo(() => {
    const total = filteredTrips.length;
    const locked = filteredTrips.filter(t => t._isLocked).length;
    const selectable = total - locked;
    // Total selected across all loaded trips (not only current filter) — accumulates across filter changes
    const selected = enriched.filter(t => selectedTripIds.has(t._tripId)).length;
    const selectedInView = filteredTrips.filter(t => selectedTripIds.has(t._tripId)).length;
    const selectedSelectable = filteredTrips.filter(t => !t._isLocked && selectedTripIds.has(t._tripId)).length;
    return { total, locked, selectable, selected, selectedInView, selectedSelectable };
  }, [filteredTrips, selectedTripIds, enriched]);

  const toggleTrip = (tripId, disabled) => {
    if (disabled) return;
    setSelectedTripIds(prev => {
      const next = new Set(prev);
      if (next.has(tripId)) next.delete(tripId);
      else next.add(tripId);
      // locked always stays selected
      lockedTripIds.forEach(id => next.add(id));
      return next;
    });
  };

  const selectAllSelectable = () => {
    setSelectedTripIds(prev => {
      const next = new Set(prev);
      // Only operate on the CURRENT filtered set (what user is looking at)
      filteredTrips.forEach(t => next.add(t._tripId));
      // Locked always stays selected globally
      lockedTripIds.forEach(id => next.add(id));
      return next;
    });
  };

  const deselectAllSelectable = () => {
    setSelectedTripIds(prev => {
      const next = new Set(prev);
      // Only deselect the CURRENT filtered set (excluding locked)
      filteredTrips.forEach(t => {
        if (!t._isLocked) next.delete(t._tripId);
      });
      lockedTripIds.forEach(id => next.add(id));
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Modernized Header & Stats - Match Driver Tab Spacing */}
      <div className="px-4 py-3 border-b border-gray-100 bg-white flex items-center justify-between gap-4 z-50 shadow-sm transition-all duration-300">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative w-[240px] shrink-0">
            <input
              type="text"
              placeholder="Search trip ID/Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-3 pr-4 h-8 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-main-primary/20 focus:border-main-primary transition-all font-medium py-0"
            />
          </div>
          
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5">
            <span className="px-2 py-1 rounded bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5 whitespace-nowrap">
              <span>Total:</span>
              <span className="text-slate-900">{stats.total}</span>
            </span>
            <span className="px-2 py-1 rounded bg-main-primary/5 border border-main-base text-main-primary text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5 whitespace-nowrap">
              <Truck className="w-3 h-3" />
              <span className="text-main-primary">{stats.selected}</span>
            </span>
            {stats.locked > 0 && (
              <span className="px-2 py-1 rounded bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5 whitespace-nowrap">
                <span>Locked:</span>
                <span className="text-orange-900">{stats.locked}</span>
              </span>
            )}
            {excludedBreakTrips > 0 && (
              <span className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5 whitespace-nowrap">
                <span>Break:</span>
                <span className="text-amber-900">{excludedBreakTrips}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchTrips}
            disabled={isLoading}
            className="group px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-slate-800 transition-all shadow-md active:scale-95 flex items-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Secondary Filters Grid - Adjusted padding */}
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/30">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <MultiSelectFilter
              labelAll="All Space Types"
              options={spaceTypeOptions}
              selected={spaceTypeFilter}
              onChange={setSpaceTypeFilter}
            />

            <MultiSelectFilter
              labelAll="All Service Areas"
              options={serviceAreaOptions}
              selected={serviceAreaFilter}
              onChange={setServiceAreaFilter}
            />

            <MultiSelectFilter
              labelAll="All funding sources"
              options={fundingOptions}
              selected={fundingFilter}
              onChange={setFundingFilter}
            />

            <div className="flex items-center gap-2 shrink-0">
              <TimePicker
                label="Start"
                value={timeStartLocal}
                onChange={(v) => setTimeStartLocal(v)}
                format="h:mm A"
                slotProps={{
                  popper: { sx: { zIndex: 2210 } },
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      '& .MuiInputBase-root': { 
                        height: 32, 
                        fontSize: '11px', 
                        width: 110, 
                        bgcolor: 'white',
                        borderRadius: '6px',
                        '&:hover': {
                          borderColor: 'rgb(var(--color-main-primary))'
                        },
                        '& fieldset': { 
                          borderColor: 'rgb(var(--color-main-base)) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgb(var(--color-main-primary)) !important',
                          borderWidth: '1px !important'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontWeight: 'bold',
                        color: 'rgb(var(--color-main-primary))'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }
                    },
                  }
                }}
              />
              <span className="text-gray-400 text-xs">-</span>
              <TimePicker
                label="End"
                value={timeEndLocal}
                onChange={(v) => setTimeEndLocal(v)}
                format="h:mm A"
                slotProps={{
                  popper: { sx: { zIndex: 2210 } },
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    sx: {
                      '& .MuiInputBase-root': { 
                        height: 32, 
                        fontSize: '11px', 
                        width: 110, 
                        bgcolor: 'white',
                        borderRadius: '6px',
                        '&:hover': {
                          borderColor: 'rgb(var(--color-main-primary))'
                        },
                        '& fieldset': { 
                          borderColor: 'rgb(var(--color-main-base)) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgb(var(--color-main-primary)) !important',
                          borderWidth: '1px !important'
                        }
                      },
                      '& .MuiInputBase-input': {
                        fontWeight: 'bold',
                        color: 'rgb(var(--color-main-primary))'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }
                    },
                  }
                }}
              />
            </div>

            <button
              type="button"
              onClick={() => { setSearchQuery(''); setSpaceTypeFilter([]); setServiceAreaFilter([]); setFundingFilter([]); setStatusFilter(''); setTimeStartLocal(null); setTimeEndLocal(null); }}
              className="px-3 h-8 shrink-0 text-[10px] font-bold uppercase tracking-wider rounded-md border border-main-base bg-white text-main-light hover:text-main-primary hover:border-main-primary hover:bg-main-primary/5 transition-all shadow-sm active:scale-95"
              title="Clear all filters"
            >
              Clear
            </button>

            <div className="ml-auto h-4 w-px bg-gray-200"></div>

            {/* Compact Status Chips - Multi-select */}
            <div className="flex flex-wrap gap-1">
              {statusChipEntries.map(({ key, count }) => {
                const isActive = !!statusFilter && key === statusFilter.toLowerCase();
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStatusFilter(prev => (prev && prev.toLowerCase() === key ? '' : key))}
                    className={`px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wider transition-all shadow-sm flex items-center gap-1 ${
                      isActive
                        ? 'bg-main-primary text-white border-main-primary'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-main-primary hover:text-main-primary hover:bg-main-primary/5'
                    }`}
                  >
                    <span>{key || '(none)'}</span>
                    <span className={`px-1 rounded-full ${isActive ? 'bg-main-primary text-white' : 'bg-gray-100 text-gray-600 group-hover:bg-main-primary/10'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </LocalizationProvider>
      </div>

      <div className="flex-1 min-h-0 overflow-auto overscroll-contain bg-gray-50/50">
        <div className="p-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-main-primary animate-spin mb-4" />
              <p className="text-main-light font-bold text-xs uppercase tracking-wide">Loading trips...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertTriangle className="w-12 h-12 text-rose-500 mb-4" />
              <p className="text-rose-600 font-bold uppercase tracking-wide">Failed to load trips</p>
              <p className="text-main-light text-sm mt-1">{error}</p>
              <button
                onClick={fetchTrips}
                className="mt-4 bg-primary text-white px-4 py-2 text-xs uppercase font-bold rounded shadow-sm"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="shadow ring-1 ring-main-base/10 rounded-lg bg-white">
              <table className="w-full min-w-[1540px] table-fixed border-separate border-spacing-0 text-left">
                <colgroup>
                  {TRIPS_COLUMNS.map((col, i) => (
                    <col key={i} style={{ width: col.width }} />
                  ))}
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    {TRIPS_COLUMNS.map((col, idx) => {
                      if (col.label === 'SELECT') {
                        const selectableInView = filteredTrips.filter(t => !t._isLocked);
                        const allSelected = selectableInView.length > 0 && selectableInView.every(t => selectedTripIds.has(t._tripId));
                        
                        return (
                          <th key={idx} className="sticky top-0 z-40 p-[6px] border-b border-r border-gray-200 bg-gray-50 select-none">
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                allSelected ? deselectAllSelectable() : selectAllSelectable();
                              }}
                              className={`mx-auto w-5 h-5 rounded-md flex items-center justify-center transition-all cursor-pointer ${allSelected ? 'bg-slate-900 shadow-sm' : 'bg-white border-2 border-slate-200 hover:border-slate-400'}`}
                            >
                              {allSelected && <Check className="w-3.5 h-3.5 text-white stroke-[4px]" />}
                            </div>
                          </th>
                        );
                      }
                      
                      return (
                        <th
                          key={idx}
                          className={`sticky top-0 z-40 p-[6px] border-b border-r border-gray-200 bg-gray-50 text-[10px] font-bold text-gray-700 uppercase tracking-wider select-none ${col.label === '#' ? 'text-center' : ''}`}
                        >
                          {col.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredTrips.map((t, idx) => {
                    const isSelected = selectedTripIds.has(t._tripId);
                    const disabled = t._isLocked;
                    const fleetDisplay = getFleetLevelDisplay(loadUnloadTimes, t.space_type_id, t.los_id);
                    const serviceAreaDisplay = getServiceAreaName(serviceAreaMap, t.service_area_id);
                    const fundingDisplay = t._fundingName;
                    const puTime = fmtTime(t.pickup_time_resolved || t.pickup_time || t.schedule_time, tz);
                    const doTime = fmtTime(t.dropoff_time_resolved || t.dropoff_time || t.appointment_time, tz);
                    const mileage = t.mileage != null && t.mileage !== '' ? Number(t.mileage).toFixed(1) : '—';
                    const displayTripId = t._displayTripId || t._tripId;

                    return (
                      <tr 
                        key={t._tripId}
                        onClick={() => toggleTrip(t._tripId, disabled)}
                        className={`group text-main-dark even:bg-main-base-50/30 hover:bg-slate-50 transition-colors border-b border-gray-100 ${disabled ? 'opacity-80' : 'cursor-pointer'}`}
                      >
                        {/* # */}
                        <td className="p-[3px] border-r border-gray-100 text-center font-black text-[10px] text-gray-400">
                          {idx + 1}
                        </td>
                        
                        {/* SELECT */}
                        <td className="p-[3px] border-r border-gray-100">
                          <div className={`mx-auto w-5 h-5 rounded-md flex items-center justify-center transition-all ${disabled ? 'bg-gray-50 border-gray-100' : isSelected ? 'bg-slate-900 shadow-sm' : 'bg-white border-2 border-slate-200 group-hover:border-slate-400'}`}>
                            {isSelected && !disabled && <Check className="w-3.5 h-3.5 text-white stroke-[4px]" />}
                            {isSelected && disabled && <Lock className="w-2.5 h-2.5 text-slate-400" />}
                            {!isSelected && disabled && <Lock className="w-2.5 h-2.5 text-slate-300" />}
                          </div>
                        </td>

                        {/* TRIP ID */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[10px] font-bold text-main-primary truncate" title={displayTripId}>{displayTripId}</div>
                        </td>

                        {/* CLIENT */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[11px] font-bold text-slate-700 truncate" title={t.member_name}>{t.member_name || '—'}</div>
                        </td>

                        {/* PU TIME */}
                        <td className="p-[3px] border-r border-gray-100 px-2 text-center">
                          <div className="text-[10px] font-medium text-slate-600 whitespace-nowrap">{puTime || '—'}</div>
                        </td>

                        {/* DO TIME */}
                        <td className="p-[3px] border-r border-gray-100 px-2 text-center">
                          <div className="text-[10px] font-medium text-slate-600 whitespace-nowrap">{doTime || '—'}</div>
                        </td>

                        {/* PICKUP ADDRESS */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[10px] text-slate-500 truncate" title={t.pickup_address || '—'}>{t.pickup_address || '—'}</div>
                        </td>

                        {/* DROPOFF ADDRESS */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[10px] text-slate-500 truncate" title={t.dropoff_address || '—'}>{t.dropoff_address || '—'}</div>
                        </td>

                        {/* MI */}
                        <td className="p-[3px] border-r border-gray-100 text-right pr-2">
                          <div className="text-[10px] font-bold text-slate-700">{mileage}</div>
                        </td>

                        {/* STATUS */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[8px] font-bold uppercase text-slate-600 whitespace-nowrap border border-slate-200">
                              {t._status || '—'}
                            </span>
                          </div>
                        </td>

                        {/* FLEET / SPACE */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[9px] font-bold uppercase text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate" title={fleetDisplay}>
                            {fleetDisplay || '—'}
                          </div>
                        </td>

                        {/* SERVICE AREA */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[9px] font-bold uppercase text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate" title={serviceAreaDisplay}>
                            {serviceAreaDisplay || '—'}
                          </div>
                        </td>

                        {/* FUNDING */}
                        <td className="p-[3px] border-r border-gray-100 px-2">
                          <div className="text-[9px] font-bold uppercase text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 truncate" title={fundingDisplay}>
                            {fundingDisplay || '—'}
                          </div>
                        </td>

                        {/* DRIVER */}
                        <td className="p-[3px] px-2">
                          {t.driver_display_name ? (
                            <div className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 px-1.5 py-0.5 rounded truncate flex items-center gap-1" title={t.driver_display_name}>
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {t.driver_display_name}
                            </div>
                          ) : (
                            <div className="text-[9px] text-gray-400 italic px-1.5">Unassigned</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
